<template>
  <section class="dept-contact-card dept-modern__section">
    <div class="dept-modern">
      <article class="dept-contact-card__panel dept-modern__card">
        <div class="dept-contact-card__grid">
          <div class="dept-contact-card__main">
            <header class="dept-contact-card__header">
              <h2 v-if="heading" class="dept-contact-card__title">{{ heading }}</h2>
              <p v-if="updatedAt" class="dept-contact-card__updated">
                Обновлено: {{ updatedAt }}
              </p>
            </header>

            <div class="dept-contact-card__rows">
              <div
                v-for="(phone, idx) in phones"
                :key="'ph-' + idx"
                class="dept-contact-card__row"
              >
                <div class="dept-contact-card__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
                    <path d="M6.5 4.5h2l1.2 3.2a1 1 0 00.95.68l2.6.4a1 1 0 01.55 1.55l-1.6 1.9a12.5 12.5 0 005.7 5.7l1.9-1.6a1 1 0 011.55.55l.4 2.6a1 1 0 00.68.95L19.5 18h2a1.5 1.5 0 001.5-1.5C21 10.5 13.5 3 6.5 3A1.5 1.5 0 005 4.5v2z" stroke="currentColor" stroke-width="1.4"/>
                  </svg>
                </div>
                <div class="dept-contact-card__text">
                  <span class="dept-contact-card__label">{{ phone.label || 'Телефон' }}</span>
                  <a class="dept-contact-card__value dept-contact-card__value--link" :href="'tel:' + phoneHref(phone.value)">{{ phone.value }}</a>
                </div>
              </div>

              <div v-if="email" class="dept-contact-card__row">
                <div class="dept-contact-card__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
                    <rect x="3.5" y="5.5" width="17" height="13" rx="2" stroke="currentColor" stroke-width="1.4"/>
                    <path d="M4 7l8 6 8-6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
                  </svg>
                </div>
                <div class="dept-contact-card__text">
                  <span class="dept-contact-card__label">Электронная почта</span>
                  <a class="dept-contact-card__value dept-contact-card__value--link" :href="'mailto:' + email">{{ email }}</a>
                </div>
              </div>

              <div v-if="address" class="dept-contact-card__row">
                <div class="dept-contact-card__icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" width="18" height="18">
                    <path d="M12 21s6-5.2 6-10.5a6 6 0 10-12 0C6 15.8 12 21 12 21z" stroke="currentColor" stroke-width="1.4"/>
                    <circle cx="12" cy="10.5" r="2" stroke="currentColor" stroke-width="1.4"/>
                  </svg>
                </div>
                <div class="dept-contact-card__text">
                  <span class="dept-contact-card__label">Адрес</span>
                  <span class="dept-contact-card__value">{{ address }}</span>
                </div>
              </div>
            </div>

            <div v-if="hasMeta" class="dept-contact-card__meta">
              <p v-if="scheduleLine" class="dept-contact-card__meta-row">
                <span class="dept-contact-card__meta-key">Режим</span>
                <span class="dept-contact-card__meta-val">{{ scheduleLine }}</span>
              </p>
              <p v-if="compositionLine" class="dept-contact-card__meta-row">
                <span class="dept-contact-card__meta-key">Состав</span>
                <span class="dept-contact-card__meta-val">{{ compositionLine }}</span>
              </p>
            </div>

            <p v-if="disclaimer" class="dept-contact-card__disclaimer">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.5"/>
                <path d="M9 12l2 2 4-4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              {{ disclaimer }}
            </p>
          </div>

          <div ref="mapCol" class="dept-contact-card__map hide-in-accessibility">
            <YandexMap
              :key="'dept-map-' + mapHeight"
              class="dept-contact-card__ymap"
              fill
              :height="mapHeight"
              :scroll-zoom="false"
            />
          </div>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import YandexMap from '@/components/Map/MapYandex.vue';

export default {
  name: 'DepartmentFactsContactsRow',
  components: { YandexMap },
  props: {
    heading: { type: String, default: '' },
    updatedAt: { type: String, default: '' },
    phones: { type: Array, default: () => [] },
    email: { type: String, default: '' },
    address: { type: String, default: '' },
    mapImage: { type: String, default: '' },
    mapAlt: { type: String, default: '' },
    scheduleItems: { type: Array, default: () => [] },
    compositionItems: { type: Array, default: () => [] },
    disclaimer: { type: String, default: '' },
  },
  data() {
    return {
      mapHeight: 360,
      mapRo: null,
    };
  },
  computed: {
    scheduleLine() {
      return (this.scheduleItems || [])
        .map((i) => String(i || '').trim())
        .filter(Boolean)
        .join(' · ');
    },
    compositionLine() {
      return (this.compositionItems || [])
        .map((i) => String(i || '').trim())
        .filter(Boolean)
        .join(' · ');
    },
    hasMeta() {
      return !!(this.scheduleLine || this.compositionLine);
    },
  },
  mounted() {
    this.$nextTick(() => this.bindMapHeight());
  },
  beforeDestroy() {
    if (this.mapRo) {
      this.mapRo.disconnect();
      this.mapRo = null;
    }
  },
  methods: {
    phoneHref(value) {
      return String(value || '').replace(/[^\d+]/g, '');
    },
    bindMapHeight() {
      const el = this.$refs.mapCol;
      if (!el) return;
      const apply = () => {
        const h = Math.round(el.clientHeight || 0);
        if (h >= 200 && Math.abs(h - this.mapHeight) > 16) {
          this.mapHeight = h;
        } else if (h >= 200 && this.mapHeight < 200) {
          this.mapHeight = h;
        }
      };
      apply();
      if (typeof ResizeObserver === 'undefined') return;
      this.mapRo = new ResizeObserver(() => apply());
      this.mapRo.observe(el);
    },
  },
};
</script>

<style scoped lang="scss">
$green: #66cc66;
$green-text: #336633;
$text: #2d3436;
$muted: #6b7280;

.dept-contact-card__panel {
  overflow: hidden;
  box-shadow: 0 2px 16px rgba(102, 204, 102, 0.06);
}

.dept-contact-card__grid {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  align-items: stretch;

  @media (max-width: 767.98px) {
    grid-template-columns: 1fr;
  }
}

.dept-contact-card__main {
  padding: 22px 28px;
  display: flex;
  flex-direction: column;

  @media (max-width: 767.98px) {
    padding: 18px 16px;
  }
}

.dept-contact-card__header {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 6px 12px;
  margin-bottom: 10px;
}

.dept-contact-card__title {
  margin: 0;
  font-family: "TTDrugs", sans-serif;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  color: $green-text;

  @media (max-width: 767.98px) {
    font-size: 22px;
  }
}

.dept-contact-card__updated {
  margin: 0;
  font-family: "Circe", sans-serif;
  font-size: 12px;
  color: $muted;
}

.dept-contact-card__rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.dept-contact-card__row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.dept-contact-card__icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #eaf7ea;
  color: $green;

  svg {
    width: 18px;
    height: 18px;
  }
}

.dept-contact-card__text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  padding-top: 2px;
}

.dept-contact-card__label {
  font-family: "Circe", sans-serif;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #9ca3af;
}

.dept-contact-card__value {
  font-family: "Circe", sans-serif;
  font-size: 16px;
  line-height: 1.3;
  color: $text;
}

.dept-contact-card__value--link {
  color: $green-text;
  text-decoration: none;
  transition: opacity 0.2s;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid $green;
    outline-offset: 2px;
  }
}

.dept-contact-card__meta {
  margin-top: 0;
  padding-top: 6px;
  border-top: 1px solid #e8ece8;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.dept-contact-card__meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 8px;
  margin: 0;
  font-family: "Circe", sans-serif;
  font-size: 15px;
  line-height: 1.3;
}

.dept-contact-card__meta-key {
  flex-shrink: 0;
  font-weight: 700;
  color: $green-text;
  min-width: 4.5em;
}

.dept-contact-card__meta-val {
  color: $muted;
}

.dept-contact-card__disclaimer {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 8px 0 0;
  font-family: "Circe", sans-serif;
  font-size: 12px;
  line-height: 1.3;
  color: #7a8580;

  svg {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    margin-top: 1px;
    color: $green;
  }
}

.dept-contact-card__map {
  position: relative;
  min-height: 320px;
  align-self: stretch;
  background: #eaf7ea;
  overflow: hidden;

  @media (max-width: 767.98px) {
    min-height: 280px;
  }
}

.dept-contact-card__ymap {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
}
</style>
